// Hooks for server-derived live data: weather forecast and proactive tips.

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { templateDailyBrief, templateWeekStory, weekSummary, type Brief, type WeekStory, type WeekSummary } from '../domain/brief.ts';
import { today } from '../domain/time.ts';
import { templateInsights, type DailyInsights } from '../domain/insights.ts';
import { trends } from '../domain/trends.ts';
import { ageNow } from '../domain/age.ts';
import { proactiveTips, type Tip } from '../domain/tips.ts';
import type { Forecast } from '../domain/weather.ts';
import { api } from './api.ts';
import { useCollection, useDevice, useEvents, useHousehold, useNow, useProfile } from './hooks.ts';

let forecast: Forecast | undefined;
const listeners = new Set<() => void>();
let inflight: Promise<void> | undefined;

async function refresh(serverUrl: string, key: string) {
  if (inflight) return inflight;
  inflight = fetch(`${serverUrl}/api/weather`, { headers: { Authorization: `Bearer ${key}` } })
    .then((r) => (r.ok ? r.json() : undefined))
    .then((f: Forecast | { error: string } | undefined) => {
      if (f && 'hours' in f) {
        forecast = f;
        listeners.forEach((l) => l());
      }
    })
    .catch(() => undefined)
    .finally(() => (inflight = undefined));
  return inflight;
}

/** Hourly forecast for home, refreshed every 30 minutes. */
export function useWeather(): Forecast | undefined {
  const device = useDevice();
  useEffect(() => {
    if (!device.key) return;
    if (!forecast || Date.now() - forecast.fetchedAt > 30 * 60_000) void refresh(device.serverUrl, device.key);
    const t = setInterval(() => void refresh(device.serverUrl, device.key!), 30 * 60_000);
    return () => clearInterval(t);
  }, [device.key, device.serverUrl]);
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => forecast,
  );
}

const DISMISS_KEY = 'arlo.dismissedTips';

function readDismissed(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

/** Current proactive tips, minus any dismissed on this device. */
export function useTips(limit = 3): { tips: Tip[]; dismiss: (id: string) => void } {
  const now = useNow(5 * 60_000);
  const profile = useProfile();
  const household = useHousehold();
  const events = useEvents();
  const care = useCollection('care');
  const weather = useWeather();
  const [dismissed, setDismissed] = useState(readDismissed);
  const all = useMemo(() => proactiveTips({ profile, household, events, care, forecast: weather, now }), [profile, household, events, care, weather, now]);
  const dismiss = (id: string) => {
    const next = new Set(dismissed).add(id);
    setDismissed(next);
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify([...next].slice(-200)));
    } catch {
      /* ignore */
    }
  };
  return { tips: all.filter((t) => !dismissed.has(t.id)).slice(0, limit), dismiss };
}

// --- Morning brief & weekly story ------------------------------------------------------

const BRIEF_KEY = 'arlo.brief';

function readBrief(): Brief | undefined {
  try {
    return JSON.parse(localStorage.getItem(BRIEF_KEY) ?? 'null') ?? undefined;
  } catch {
    return undefined;
  }
}

/** Today's brief from the server (AI-written when enabled); a local template when offline. */
export function useBrief(): Brief {
  const device = useDevice();
  const now = useNow(10 * 60_000);
  const profile = useProfile();
  const household = useHousehold();
  const events = useEvents();
  const care = useCollection('care');
  const weather = useWeather();
  const day = today(now);
  // Fetch again once it's morning: before 6am the server only has a placeholder.
  const morning = new Date(now).getHours() >= 6;
  const [server, setServer] = useState<Brief | undefined>(() => {
    const b = readBrief();
    return b?.date === day ? b : undefined;
  });
  useEffect(() => {
    if (!device.key) return;
    let live = true;
    api<Brief>('/api/brief')
      .then((b) => {
        if (!live) return;
        setServer(b);
        try {
          localStorage.setItem(BRIEF_KEY, JSON.stringify(b));
        } catch {
          /* ignore */
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [device.key, day, morning]);
  const appointments = useCollection('appointments');
  const trainingWeeks = useCollection('trainingWeeks');
  const local = useMemo(() => templateDailyBrief({ profile, household, events, care, forecast: weather, now, appointments, trainingWeeks }), [profile, household, events, care, weather, now, appointments, trainingWeeks]);
  return server?.date === day ? server : local;
}

export type WeekStoryResult = WeekStory & { summary?: WeekSummary };

/** The last 7 days as a story (server, AI when enabled), or the local template. */
export function useWeekStory(): { story: WeekStoryResult; loading: boolean; refresh: () => void } {
  const device = useDevice();
  const now = useNow(30 * 60_000);
  const profile = useProfile();
  const events = useEvents();
  const [server, setServer] = useState<WeekStoryResult | undefined>();
  const [loading, setLoading] = useState(false);
  const load = (refresh = false) => {
    if (!device.key) return;
    setLoading(true);
    api<WeekStoryResult>(`/api/brief?kind=week${refresh ? '&refresh=1' : ''}`)
      .then(setServer)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  };
  const day = today(now);
  useEffect(() => load(), [device.key, day]);
  const local = useMemo(() => {
    const summary = weekSummary(events, profile, now);
    return { ...templateWeekStory(summary, profile.name), summary };
  }, [events, profile, now]);
  return { story: server ?? local, loading, refresh: () => load(true) };
}

/** Today's insights: the server's (AI-written once a day when enabled), else the rule-based ones from his logs. */
export function useDailyInsights(): { insights: DailyInsights; loading: boolean; refresh: () => void } {
  const device = useDevice();
  const now = useNow(15 * 60_000);
  const profile = useProfile();
  const household = useHousehold();
  const events = useEvents();
  const [server, setServer] = useState<DailyInsights | undefined>();
  const [loading, setLoading] = useState(false);
  const day = today(now);
  const load = (refresh = false) => {
    if (!device.key) return;
    setLoading(true);
    api<DailyInsights>(`/api/brief?kind=insights${refresh ? '&refresh=1' : ''}`)
      .then(setServer)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  };
  useEffect(() => load(), [device.key, day]);
  const local = useMemo(() => {
    const weeks = ageNow(profile, now)?.weeks ?? 9;
    return templateInsights(trends(events, now, weeks, { start: household.nightStart, end: household.nightEnd }), now);
  }, [events, profile, household.nightStart, household.nightEnd, now]);
  return { insights: server?.date === day ? server : local, loading, refresh: () => load(true) };
}
